import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { GitBranch } from 'lucide-react';
import apiClient from '@/api/client';
import { Card } from './backupShared';
import { announceTreeRestore } from './treeRestoreResult';
import { TreeRestorePicker, type Inspection, type RestoreChoice } from './TreeRestorePicker';

// ─── Einen einzelnen Stammbaum aus einem hochgeladenen Gesamt-Backup ─────────
//
// Der häufigere Fall als „alles zurück": in einem Baum wurde versehentlich
// gelöscht, während an den anderen weitergearbeitet wurde. Ein vollständiges
// Einspielen würde deren Arbeit mitlöschen.
//
// Zwei Schritte, zwei Uploads. Der erste liest die Datei nur (und ändert
// nichts), der zweite spielt ein. Das kostet einen zweiten Upload, hält den
// Server aber zustandslos – hochgeladene Backups liegen nicht zwischen zwei
// Klicks irgendwo herum.

export function TreeFromGlobalBackup() {
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [info, setInfo] = useState<Inspection | null>(null);
  const [busy, setBusy] = useState<null | 'inspect' | 'restore'>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setFile(null);
    setInfo(null);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const handleFile = async (f: File) => {
    setFile(f);
    setInfo(null);
    setBusy('inspect');
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await apiClient.post<Inspection>('/backup/trees', form);
      setInfo(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Backup konnte nicht gelesen werden.');
      reset();
    } finally {
      setBusy(null);
    }
  };

  const handleRestore = async (choice: RestoreChoice) => {
    if (!file) return;
    setBusy('restore');
    try {
      const form = new FormData();
      form.append('file', file);
      for (const [k, v] of Object.entries(choice)) {
        if (v !== undefined) form.append(k, v);
      }
      const { data } = await apiClient.post('/backup/restore-tree', form);
      announceTreeRestore(data);
      await queryClient.invalidateQueries();
      reset();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Wiederherstellung fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      title="Einzelnen Stammbaum aus einer Backup-Datei holen"
      description="Nimmt ein Gesamt-Backup und spielt daraus nur einen Stammbaum ein. Die übrigen Stammbäume bleiben unberührt – anders als beim vollständigen Wiederherstellen."
      icon={GitBranch}
    >
      <input
        ref={fileRef}
        type="file"
        accept="application/zip,.zip"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
        className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-200"
      />

      {busy === 'inspect' && <p className="mt-3 text-xs text-slate-500">Backup wird gelesen…</p>}

      {info && (
        <TreeRestorePicker
          info={info}
          busy={busy === 'restore'}
          onRestore={(c) => void handleRestore(c)}
          onCancel={reset}
        />
      )}
    </Card>
  );
}
